import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import HowItWorks from '../components/HowItWorks';
import { SEEKER_STEPS } from '../data/onboardingData';
import { API as API_V1, MEDIA_URL } from '../config';
import './Landing.css';

const API = API_V1;
const API_BASE = MEDIA_URL;

function avatarSrc(url) {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${API_BASE}/${url.replace(/^\/+/, '')}`;
}

function Landing() {
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [reviewsLoaded, setReviewsLoaded] = useState(false);
  const [showAllReviews, setShowAllReviews] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000); // 8s max

    fetch(`${API}/reviews/`, { signal: controller.signal })
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(data => { setReviews(Array.isArray(data) ? data : []); setReviewsLoaded(true); })
      .catch(err => { console.warn('Reviews fetch failed:', err); setReviewsLoaded(true); })
      .finally(() => clearTimeout(timeout));
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > window.innerHeight * 0.8) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleGetStarted = () => {
    navigate('/register');
  };

  return (
    <div className="landing-page">
      {/* Navigation */}
      <nav className={`landing-nav ${isScrolled ? 'scrolled' : ''}`}>
        <div className="nav-container">
          <div className="logo" onClick={() => navigate('/')}>
            <img src="/JobLinkedAI.png" alt="JobLinked AI" />
            JobLinked AI
          </div>
          <ul className="nav-links">
            <li><a href="#features">Features</a></li>
            <li><a href="#pricing">Pricing</a></li>
            <li><a href="#about">How It Works</a></li>
            <li><button className="btn-nav" onClick={() => navigate('/login')}>Login</button></li>
          </ul>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero">
        <div className="stars">
          {[...Array(20)].map((_, i) => (
            <div key={i} className="star"></div>
          ))}
        </div>
        <div className="hero-container">
          <div className="hero-content">
            <h1>Connect with Top Employers</h1>
            <p>JobLinked AI matches you with jobs posted directly by South African recruiters. Apply with AI-powered insights and track your applications in one place.</p>
            <div className="hero-buttons">
              <button className="btn btn-primary btn-large" onClick={handleGetStarted}>Get Started Free</button>
              <button className="btn btn-secondary" onClick={() => document.getElementById('features').scrollIntoView({ behavior: 'smooth' })}>Watch Demo</button>
              <button className="btn btn-recruiters" onClick={() => navigate('/recruiters')}>
                <i className="fas fa-building"></i> For Recruiters
              </button>
            </div>
            <div style={{ marginTop: '2rem', opacity: 0.9 }}>
              <p style={{ fontSize: '0.9rem' }}>✓ Direct recruiter connections  ✓ AI-powered matching  ✓ Track everything</p>
            </div>
          </div>
          <div className="hero-image">
            <div className="hero-card">
              <img src="/JobLinkedAI.png" alt="JobLinked AI" />
              <h3>Your AI Job Assistant</h3>
              <p>Powered by advanced machine learning</p>
            </div>
          </div>
        </div>
        <div className="waves">
          <div className="wave"></div>
          <div className="wave"></div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features" id="features">
        <div className="section-header">
          <h2>Why JobLinked AI?</h2>
          <p>Connect directly with recruiters and let AI help you find the perfect match. Focus on preparing for interviews while we handle the rest.</p>
        </div>
        <div className="features-grid">
          <div className="feature-card">
            <div className="feature-icon"><i className="fas fa-robot"></i></div>
            <h3>AI-Powered Matching</h3>
            <p>Our ML algorithm analyzes your CV and matches you with jobs that fit your skills, experience, and career goals with 95%+ accuracy.</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon"><i className="fas fa-bolt"></i></div>
            <h3>Auto-Apply</h3>
            <p>Apply to 50+ jobs automatically while you sleep. We fill out forms, attach your CV, and submit applications on your behalf.</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon"><i className="fas fa-chart-line"></i></div>
            <h3>Track Everything</h3>
            <p>See all your applications, responses, and interviews in one beautiful dashboard. Never lose track of where you applied.</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon"><i className="fas fa-file-alt"></i></div>
            <h3>CV Optimization</h3>
            <p>AI rewrites your CV for each job to match keywords and requirements, increasing your chances of getting noticed.</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon"><i className="fas fa-bell"></i></div>
            <h3>Smart Notifications</h3>
            <p>Get instant alerts when companies view your profile, respond to applications, or schedule interviews.</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon"><i className="fas fa-briefcase"></i></div>
            <h3>Direct from Recruiters</h3>
            <p>Browse jobs posted directly by South African companies and recruiters. Apply with confidence knowing your application goes straight to the hiring team.</p>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="about">
        <HowItWorks steps={SEEKER_STEPS} autoAdvanceMs={4000} />
      </section>

      {/* Pricing Section */}
      <section className="pricing" id="pricing">
        <div className="section-header">
          <h2>Simple, Transparent Pricing</h2>
          <p>Choose the plan that works for you. No hidden fees, cancel anytime.</p>
        </div>
        <div className="pricing-grid">
          <div className="pricing-card">
            <h3>Free</h3>
            <div className="pricing-price">R0</div>
            <div className="pricing-period">Forever</div>
            <ul className="pricing-features">
              <li><i className="fas fa-check"></i> 5 job matches/month</li>
              <li><i className="fas fa-check"></i> Manual applications</li>
              <li><i className="fas fa-check"></i> Basic tracking</li>
              <li><i className="fas fa-check"></i> Email notifications</li>
            </ul>
            <button className="btn" onClick={handleGetStarted}>Get Started</button>
          </div>
          <div className="pricing-card featured">
            <div className="pricing-badge">Most Popular</div>
            <h3>Pro</h3>
            <div className="pricing-price">R299</div>
            <div className="pricing-period">per month</div>
            <ul className="pricing-features">
              <li><i className="fas fa-check"></i> 50 job matches/month</li>
              <li><i className="fas fa-check"></i> 50 auto-applications/month (3/day)</li>
              <li><i className="fas fa-check"></i> AI job matching</li>
              <li><i className="fas fa-check"></i> Full tracking dashboard</li>
              <li><i className="fas fa-check"></i> Email + SMS alerts</li>
              <li><i className="fas fa-check"></i> Interview prep tips</li>
              <li><i className="fas fa-check"></i> Priority support</li>
            </ul>
            <button className="btn btn-primary" onClick={() => navigate('/register')}>Get Started</button>
          </div>
          <div className="pricing-card premium">
            <div className="premium-stars">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="premium-star"></div>
              ))}
            </div>
            <h3>Premium</h3>
            <div className="pricing-price">R599</div>
            <div className="pricing-period">per month</div>
            <ul className="pricing-features">
              <li><i className="fas fa-check"></i> Unlimited job matches</li>
              <li><i className="fas fa-check"></i> Unlimited auto-applications (3/day)</li>
              <li><i className="fas fa-check"></i> AI job matching</li>
              <li><i className="fas fa-check"></i> AI CV optimisation</li>
              <li><i className="fas fa-check"></i> Interview prep tips</li>
              <li><i className="fas fa-check"></i> Salary insights</li>
              <li><i className="fas fa-check"></i> Email + SMS alerts</li>
              <li><i className="fas fa-check"></i> Dedicated support</li>
            </ul>
            <button className="btn" onClick={() => navigate('/register')}>Get Started</button>
          </div>
        </div>
      </section>

      {/* Reviews Section — only render while loading or when there are reviews */}
      {(!reviewsLoaded || reviews.length > 0) && (
        <section className="reviews-section" id="reviews">
          <div className="section-header">
            <h2>What People Say About JobLinked AI</h2>
            <p>Real feedback from job seekers and recruiters across South Africa</p>
          </div>
          {!reviewsLoaded ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7280' }}>
              <i className="fas fa-spinner fa-spin" style={{ fontSize: '1.5rem', display: 'block', marginBottom: '0.75rem' }}></i>
            </div>
          ) : (
            <>
              <div className="reviews-grid">
                {(showAllReviews ? reviews : reviews.slice(0, 6)).map(r => (
                  <div key={r.id} className="review-card">
                    <div className="review-author">
                      <div className="review-avatar">
                        {r.avatar_url
                          ? <img
                              src={avatarSrc(r.avatar_url)}
                              alt={r.full_name}
                              style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }}
                              onError={e => { e.currentTarget.style.display = 'none'; }}
                            />
                          : null
                        }
                        {!r.avatar_url && r.full_name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="review-name">{r.full_name}</div>
                        <div className="review-role">{r.company_or_title || (r.user_type === 'RECRUITER' ? 'Recruiter' : 'Job Seeker')}</div>
                      </div>
                      <div className="review-date">{new Date(r.created_at).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })}</div>
                    </div>
                    <div className="review-stars">
                      {[1,2,3,4,5].map(s => (
                        <i key={s} className="fas fa-star" style={{ color: s <= r.rating ? '#f59e0b' : '#e2e8f0', fontSize: '0.9rem' }}></i>
                      ))}
                    </div>
                    <p className="review-text">"{r.review_text}"</p>
                  </div>
                ))}
              </div>
              {reviews.length > 6 && (
                <div style={{ textAlign: 'center', marginTop: '2rem' }}>
                  <button className="btn" style={{ background: 'linear-gradient(135deg, var(--cobalt), var(--capri))', color: 'white', padding: '0.75rem 2.5rem', borderRadius: '50px', fontSize: '1rem' }}
                    onClick={() => setShowAllReviews(v => !v)}>
                    {showAllReviews ? 'Show Less' : `Show More (${reviews.length - 6} more)`}
                  </button>
                </div>
              )}
            </>
          )}
        </section>
      )}

      {/* CTA Section */}
      <section className="cta">
        <div className="stars">
          {[...Array(10)].map((_, i) => (
            <div key={i} className="star"></div>
          ))}
        </div>
        <h2>Ready to Land Your Dream Job?</h2>
        <p>Join thousands of South Africans connecting with top employers through JobLinked AI</p>
        <button className="btn btn-primary" onClick={() => navigate('/register')}>Start Your Free Trial</button>
        <p style={{ marginTop: '1rem', fontSize: '0.9rem', opacity: 0.8 }}>No credit card required • 7-day free trial • Cancel anytime</p>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <p>&copy; 2025 JobLinked AI. All rights reserved.</p>
        <p style={{ marginTop: '0.5rem' }}>
          <a href="/terms" style={{ color: 'rgba(255,255,255,0.6)', marginRight: '1.5rem', textDecoration: 'none', fontSize: '0.85rem' }}>Terms &amp; Conditions</a>
          <a href="/privacy" style={{ color: 'rgba(255,255,255,0.6)', marginRight: '1.5rem', textDecoration: 'none', fontSize: '0.85rem' }}>Privacy Policy</a>
          <a href="/cookies" style={{ color: 'rgba(255,255,255,0.6)', textDecoration: 'none', fontSize: '0.85rem' }}>Cookie Policy</a>
        </p>
        <p style={{ marginTop: '0.75rem' }}>Built with ❤️ in South Africa</p>
      </footer>
    </div>
  );
}

export default Landing;
